import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { SignatureSettings, DEFAULT_CHECKBOX_TEXT } from '../../types/waiver';

const Container = styled.div``;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
  margin: 0 0 20px 0;
`;

const OptionCard = styled.div<{ $selected: boolean }>`
  border: 2px solid ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.border)};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ theme, $selected }) => ($selected ? 'rgba(0, 212, 255, 0.1)' : 'transparent')};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Radio = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const OptionLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const OptionDescription = styled.p`
  margin: 8px 0 0 30px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: 20px 0;
`;

const Label = styled.label`
  display: block;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
  margin-bottom: 6px;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 40px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  resize: none;
  overflow: hidden;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

interface SignatureSettingsEditorProps {
  settings: SignatureSettings;
  onChange: (settings: SignatureSettings) => void;
}

type SignatureMode = 'graphical' | 'checkbox' | 'both';

const SignatureSettingsEditor: React.FC<SignatureSettingsEditorProps> = ({
  settings,
  onChange,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };

  useEffect(() => {
    autoResize();
  }, [settings.checkboxText]);

  const getSelectedMode = (): SignatureMode => {
    if (settings.graphicalSignature && settings.checkboxAgreement) return 'both';
    if (settings.graphicalSignature) return 'graphical';
    return 'checkbox';
  };

  const handleModeChange = (mode: SignatureMode) => {
    switch (mode) {
      case 'graphical':
        onChange({
          ...settings,
          graphicalSignature: true,
          checkboxAgreement: false,
          requireBoth: false,
        });
        break;
      case 'checkbox':
        onChange({
          ...settings,
          graphicalSignature: false,
          checkboxAgreement: true,
          requireBoth: false,
        });
        break;
      case 'both':
        onChange({
          ...settings,
          graphicalSignature: true,
          checkboxAgreement: true,
          requireBoth: settings.requireBoth,
        });
        break;
    }
  };

  const handleCheckboxTextChange = (text: string) => {
    onChange({ ...settings, checkboxText: text });
  };

  const selectedMode = getSelectedMode();

  return (
    <Container>
      <Description>
        Choose how players will sign the waiver. You can require a graphical signature,
        a simple checkbox agreement, or both.
      </Description>

      <OptionCard
        $selected={selectedMode === 'graphical'}
        onClick={() => handleModeChange('graphical')}
      >
        <OptionHeader>
          <Radio
            type="radio"
            name="signatureMode"
            checked={selectedMode === 'graphical'}
            onChange={() => handleModeChange('graphical')}
          />
          <OptionLabel>Graphical Signature Only</OptionLabel>
        </OptionHeader>
        <OptionDescription>
          Players must draw their signature on screen. Provides a more formal agreement record.
        </OptionDescription>
      </OptionCard>

      <OptionCard
        $selected={selectedMode === 'checkbox'}
        onClick={() => handleModeChange('checkbox')}
      >
        <OptionHeader>
          <Radio
            type="radio"
            name="signatureMode"
            checked={selectedMode === 'checkbox'}
            onChange={() => handleModeChange('checkbox')}
          />
          <OptionLabel>Checkbox Agreement Only</OptionLabel>
        </OptionHeader>
        <OptionDescription>
          Players check a box to agree. Faster and simpler, good for mobile devices.
        </OptionDescription>
      </OptionCard>

      <OptionCard
        $selected={selectedMode === 'both'}
        onClick={() => handleModeChange('both')}
      >
        <OptionHeader>
          <Radio
            type="radio"
            name="signatureMode"
            checked={selectedMode === 'both'}
            onChange={() => handleModeChange('both')}
          />
          <OptionLabel>Both Options</OptionLabel>
        </OptionHeader>
        <OptionDescription>
          Players can choose between graphical signature or checkbox agreement.
        </OptionDescription>
      </OptionCard>

      {(selectedMode === 'checkbox' || selectedMode === 'both') && (
        <>
          <Divider />
          <Label>Agreement Text</Label>
          <Textarea
            ref={textareaRef}
            value={settings.checkboxText ?? DEFAULT_CHECKBOX_TEXT}
            onChange={(e) => handleCheckboxTextChange(e.target.value)}
            onInput={autoResize}
            placeholder={DEFAULT_CHECKBOX_TEXT}
          />
        </>
      )}
    </Container>
  );
};

export default SignatureSettingsEditor;
